import axios from 'axios';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './baseQuery';
import { apiConstants, postMultipart, patch } from '../common/constants';
import {
  startSetup, startUpdate,
  initFromContext, hydrateTatConfig, applyExtractionResult,
  setStandardTat, startExtraction, finishExtraction,
  setUploadResults, replaceUploadResult,
} from './tatWizardSlice';

export const tatApiSlice = createApi({
  reducerPath: 'tatApi',
  baseQuery,
  tagTypes: ['TatMerchants', 'TatConfig'],
  endpoints: (builder) => ({

    // ── API 1 — Dashboard: list merchants for TAT setup ───────────────────────
    getTatMerchants: builder.query({
      queryFn: async ({ tat_status, page = 1, limit = 50 } = {}, _api, _extra, bq) => {
        try {
          const params = new URLSearchParams({ page, limit });
          if (tat_status) params.set('tat_status', tat_status);
          const res = await bq({ url: `${apiConstants.TAT_MERCHANTS}?${params}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      transformResponse: (res) => res?.data ? res : { data: res?.data || [], pagination: res?.pagination || {} },
      providesTags: ['TatMerchants'],
    }),

    // ── API 2 — TAT wizard context (Stage 0) ──────────────────────────────────
    getTatMerchantContext: builder.query({
      queryFn: async (merchantOid, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${apiConstants.TAT_MERCHANTS}/${merchantOid}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const ctx = data?.data ?? data;
          if (ctx?.oid) dispatch(initFromContext(ctx));
        } catch { }
      },
    }),

    // ── API 3 — Load existing TAT config (update flow) ────────────────────────
    getTatConfig: builder.query({
      queryFn: async (merchantOid, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${apiConstants.TAT_MERCHANTS}/${merchantOid}/config`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          // 404 = no existing config — normal for first-time setup, not an error
          if (err?.response?.status === 404) return { data: null };
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const cfg = data?.data ?? data;
          if (cfg) dispatch(hydrateTatConfig(cfg));
        } catch { }
      },
      providesTags: ['TatConfig'],
    }),

    // ── API 4 — AI extract TAT from PDF ──────────────────────────────────────
    aiExtractTat: builder.mutation({
      queryFn: async ({ merchantOid, file, activeSts }, api, _extra, _bq) => {
        api.dispatch(startExtraction());
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (activeSts?.length) formData.append('active_sts', JSON.stringify(activeSts));
          const res = await postMultipart(
            `${apiConstants.TAT_MERCHANTS}/${merchantOid}/ai-extract`,
            formData,
          );
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const result = data?.data ?? data;
          dispatch(finishExtraction({ hasTatTable: result.has_tat_table, confidence: result.confidence }));
          dispatch(applyExtractionResult(result));
        } catch {
          dispatch(finishExtraction({ hasTatTable: false, confidence: 0 }));
        }
      },
    }),

    // ── API 5 — Standard TAT master (industry reference values) ───────────────
    getTatStandard: builder.query({
      queryFn: async (_arg, _api, _extra, bq) => {
        try {
          const res = await bq({ url: apiConstants.TAT_STANDARD, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const standard = data?.data ?? data;
          if (standard) dispatch(setStandardTat(standard));
        } catch { }
      },
    }),

    // ── API 6 — Upload TAT CSV (one call per ST × account code) ──────────────
    // Uses axios directly to support custom X-* headers that the baseQuery doesn't expose.
    uploadTatCsv: builder.mutation({
      queryFn: async ({ merchantOid, serviceType, accountCode, pricingMode, csvBody, fileRecord }) => {
        try {
          const token = localStorage.getItem('userToken');
          const res = await axios.post(
            `${apiConstants.BASE_LOCAL}${apiConstants.TAT_MERCHANTS}/${merchantOid}/upload`,
            csvBody,
            {
              headers: {
                'Content-Type': 'text/plain',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'X-Service-Type': serviceType,
                'X-Account-Code': accountCode || '',
                'X-Pricing-Mode': pricingMode || 'courier_wise',
              },
            },
          );
          return { data: res.data ?? res };
        } catch (err) {
          // Return structured error so the caller can render inline fix UI
          const errData = err?.response?.data || {};
          return {
            data: {
              status: 'error',
              code: err?.response?.status || 500,
              error_mode: errData.error_mode || 'server_error',
              message: errData.message || err.message || 'Upload failed',
              accountCode: fileRecord?.accountCode,
              parent: fileRecord?.parent,
              service_type: serviceType,
              mode: pricingMode || 'courier_wise',
            },
          };
        }
      },
      onQueryStarted: async ({ fileRecord }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const result = {
            filename: fileRecord?.name || '',
            accountCode: data.accountCode ?? fileRecord?.accountCode,
            parent: data.parent ?? fileRecord?.parent,
            service_type: data.service_type ?? fileRecord?.service_type,
            mode: data.mode ?? fileRecord?.mode,
            status: data.status,
            code: data.code,
            message: data.message,
            ...(data.error_mode && { error_mode: data.error_mode }),
          };
          dispatch(replaceUploadResult(result));
        } catch { }
      },
    }),

    // ── API 7 — Mark TAT live ─────────────────────────────────────────────────
    markTatLive: builder.mutation({
      queryFn: async ({ merchantOid, publishedSts, fileCount, publishedAt }) => {
        try {
          const res = await patch(`${apiConstants.TAT_MERCHANTS}/${merchantOid}/status`, {
            tat_status: 'live',
            published_sts: publishedSts,
            file_count: fileCount,
            published_at: publishedAt || new Date().toISOString(),
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      invalidatesTags: ['TatMerchants', 'TatConfig'],
    }),

  }),
});

export const {
  useGetTatMerchantsQuery,
  useLazyGetTatMerchantContextQuery,
  useLazyGetTatConfigQuery,
  useAiExtractTatMutation,
  useGetTatStandardQuery,
  useUploadTatCsvMutation,
  useMarkTatLiveMutation,
} = tatApiSlice;
